import { AlertTriangle, X } from 'lucide-react';
import type { ParsedEvent } from '@/lib/types';
import { TagBadge } from '@/components/shared/TagBadge';
import { cn } from '@/lib/utils';

interface EventCardProps {
  event: ParsedEvent;
  index: number;
  onChange: (index: number, updated: ParsedEvent) => void;
  onDelete: (index: number) => void;
  weddingDate: string;
}

function extractTime(iso: string): string {
  // "HH:MM" from ISO string
  if (!iso) return '00:00';
  return iso.slice(11, 16);
}

function replaceTime(_iso: string, time: string, date: string): string {
  return `${date}T${time}:00`;
}

const inputBase: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  outline: 'none',
  color: 'var(--color-text)',
  fontFamily: 'var(--font-body)',
  width: '100%',
};

export function EventCard({ event, index, onChange, onDelete, weddingDate }: EventCardProps) {
  const hasLowConfidence = event.confidence < 0.7;

  function handleNameChange(e: React.ChangeEvent<HTMLInputElement>) {
    onChange(index, { ...event, name: e.target.value });
  }

  function handleLocationChange(e: React.ChangeEvent<HTMLInputElement>) {
    onChange(index, { ...event, location: e.target.value || null });
  }

  function handleStartTimeChange(e: React.ChangeEvent<HTMLInputElement>) {
    const newIso = replaceTime(event.startTime, e.target.value, weddingDate);
    onChange(index, { ...event, startTime: newIso });
  }

  function handleEndTimeChange(e: React.ChangeEvent<HTMLInputElement>) {
    const newIso = replaceTime(event.endTime, e.target.value, weddingDate);
    onChange(index, { ...event, endTime: newIso });
  }

  return (
    <div
      className={cn('relative')}
      style={{
        paddingTop: 12,
        paddingBottom: 12,
        borderBottom: '1px solid var(--color-border)',
      }}
    >
      {/* Top row: name + delete */}
      <div className="flex items-start gap-2" style={{ marginBottom: 8 }}>
        <input
          type="text"
          value={event.name}
          onChange={handleNameChange}
          placeholder="Event name"
          style={{
            ...inputBase,
            fontSize: 15,
            fontWeight: 600,
            flex: 1,
            padding: 0,
          }}
          onFocus={(e) => {
            (e.currentTarget as HTMLInputElement).style.outline = `2px solid var(--color-primary)`;
            (e.currentTarget as HTMLInputElement).style.outlineOffset = '2px';
            (e.currentTarget as HTMLInputElement).style.borderRadius = '4px';
          }}
          onBlur={(e) => {
            (e.currentTarget as HTMLInputElement).style.outline = 'none';
          }}
        />
        <button
          onClick={() => onDelete(index)}
          aria-label="Delete event"
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--color-text-secondary)',
            padding: 0,
            width: 20,
            height: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <X size={16} />
        </button>
      </div>

      {/* Time row */}
      <div
        className="flex items-center gap-2"
        style={{ marginBottom: 8, fontSize: 13, color: 'var(--color-text-secondary)' }}
      >
        <input
          type="time"
          value={extractTime(event.startTime)}
          onChange={handleStartTimeChange}
          style={{
            ...inputBase,
            width: 'auto',
            fontSize: 13,
            color: 'var(--color-text-secondary)',
            fontFamily: 'var(--font-mono)',
            cursor: 'pointer',
          }}
        />
        <span style={{ color: 'var(--color-text-muted)' }}>&ndash;</span>
        <input
          type="time"
          value={extractTime(event.endTime)}
          onChange={handleEndTimeChange}
          style={{
            ...inputBase,
            width: 'auto',
            fontSize: 13,
            color: 'var(--color-text-secondary)',
            fontFamily: 'var(--font-mono)',
            cursor: 'pointer',
          }}
        />
        {event.endTimeEstimated && (
          <span
            style={{
              fontSize: 10,
              color: 'var(--color-primary)',
              fontStyle: 'italic',
              padding: '1px 6px',
              borderRadius: 4,
              background: 'var(--color-primary-muted)',
              flexShrink: 0,
            }}
          >
            estimated
          </span>
        )}
      </div>

      {/* Category row */}
      <div className="flex items-center gap-2" style={{ marginBottom: 8 }}>
        {hasLowConfidence ? (
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              padding: '2px 6px 2px 4px',
              border: '2px dashed rgba(251,191,36,0.5)',
              borderRadius: 6,
            }}
          >
            <AlertTriangle size={12} style={{ color: '#FBBF24', flexShrink: 0 }} />
            <TagBadge category={event.category} />
            <span style={{ fontSize: 10, color: '#FBBF24' }}>Low confidence</span>
          </div>
        ) : (
          <TagBadge category={event.category} />
        )}
      </div>

      {/* Location row */}
      <div>
        <input
          type="text"
          value={event.location ?? ''}
          onChange={handleLocationChange}
          placeholder="Location (optional)"
          style={{
            ...inputBase,
            fontSize: 13,
            color: 'var(--color-text-secondary)',
          }}
          onFocus={(e) => {
            (e.currentTarget as HTMLInputElement).style.outline = `2px solid var(--color-primary)`;
            (e.currentTarget as HTMLInputElement).style.outlineOffset = '2px';
            (e.currentTarget as HTMLInputElement).style.borderRadius = '4px';
          }}
          onBlur={(e) => {
            (e.currentTarget as HTMLInputElement).style.outline = 'none';
          }}
        />
      </div>
    </div>
  );
}
